'use client';

import type { ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  Badge,
  BodyDefault,
  EntityIcon,
  IconButton,
  MetaDefault,
  Tooltip,
  getEndpointAssetTitle,
  getEndpointOperationName,
  isAiEndpoint,
  resolveUnverifiedAssetStatusLabelKey,
  resolveUnverifiedAssetStatusTooltipKey,
  type EntityIconType,
} from '@terra/ui-design-system';
import { HierarchyIcon } from '@terra/ui-icons';
import type {
  AssetPreviewDto,
  UnverifiedAssetStatus,
} from '@terra/shared-contracts-assets';
import {
  isArchivedLifecycle,
  isCodeDeletedLifecycle,
} from '@terra/shared-dto-types';
import { useAssetsTableContext } from './AssetsTableContext';
import { rowClickBlockerProps } from '../rowClickBlocker';

type EndpointAssetPreview = Extract<AssetPreviewDto, { assetType: 'endpoint' }>;
type WebpageAssetPreview = Extract<AssetPreviewDto, { assetType: 'webpage' }>;
type UnverifiedEndpointAssetPreview = Extract<
  AssetPreviewDto,
  { assetType: 'unverifiedEndpoint' }
>;

function AssetNameLayout({
  iconType,
  title,
  subtitle,
  badges,
}: {
  iconType: EntityIconType;
  title: string;
  subtitle: string | null;
  badges: ReactNode;
}) {
  return (
    <HStack gap={2} h="full" alignItems="center" minW={0} w="full">
      <Box flexShrink={0} display="inline-flex">
        <EntityIcon
          type={iconType}
          appearance="highlighted"
          density="compact"
        />
      </Box>
      <VStack gap={0} alignItems="stretch" minW={0} flex={1}>
        <HStack gap={1.5} minW={0}>
          <BodyDefault truncate minW={0} title={title}>
            {title}
          </BodyDefault>
          {badges}
        </HStack>
        {subtitle ? (
          <MetaDefault color="fg.subtle" truncate title={subtitle}>
            {subtitle}
          </MetaDefault>
        ) : null}
      </VStack>
    </HStack>
  );
}

const ASSET_NAME_BADGE_KIND = {
  NEW: 'new',
  OUT_OF_SCOPE: 'outOfScope',
  DELETED_FROM_CODE: 'deletedFromCode',
} as const;

type AssetNameBadgeKind =
  | (typeof ASSET_NAME_BADGE_KIND)[keyof typeof ASSET_NAME_BADGE_KIND]
  | UnverifiedAssetStatus;

function resolveAssetNameStatusCopy({
  kind,
  tCards,
  tAssets,
  tCommon,
}: {
  kind: AssetNameBadgeKind;
  tCards: (key: 'new') => string;
  tAssets: (
    key:
      | ReturnType<typeof resolveUnverifiedAssetStatusLabelKey>
      | NonNullable<ReturnType<typeof resolveUnverifiedAssetStatusTooltipKey>>,
  ) => string;
  tCommon: (key: 'deletedFromCode' | 'outOfScope') => string;
}): { label: string; tooltip?: string } {
  switch (kind) {
    case ASSET_NAME_BADGE_KIND.NEW:
      return { label: tCards('new') };
    case ASSET_NAME_BADGE_KIND.OUT_OF_SCOPE:
      return { label: tCommon('outOfScope') };
    case ASSET_NAME_BADGE_KIND.DELETED_FROM_CODE:
      return { label: tCommon('deletedFromCode') };
    case 'FAILED':
    case 'BLOCKED':
    case 'REJECTED':
    case 'PENDING': {
      const tooltipKey = resolveUnverifiedAssetStatusTooltipKey({
        unverifiedStatus: kind,
      });

      return {
        label: tAssets(
          resolveUnverifiedAssetStatusLabelKey({
            unverifiedStatus: kind,
          }),
        ),
        tooltip: tooltipKey ? tAssets(tooltipKey) : undefined,
      };
    }

    default: {
      const _exhaustive: never = kind;

      return _exhaustive;
    }
  }
}

function AssetNameStatusBadge({ kind }: { kind: AssetNameBadgeKind }) {
  const tCards = useTranslations('designSystem.applicationAssets.cards');
  const tAssets = useTranslations('designSystem.applicationAssets');
  const tCommon = useTranslations('designSystem.common');
  const copy = resolveAssetNameStatusCopy({
    kind,
    tCards,
    tAssets,
    tCommon,
  });

  return (
    <Tooltip title={copy.tooltip} maxW="320px">
      <Badge variant="emphasis" size="2xs" flexShrink={0}>
        {copy.label}
      </Badge>
    </Tooltip>
  );
}

function AssetNameBadges({ asset }: { asset: AssetPreviewDto }) {
  const { newAssetIds } = useAssetsTableContext();
  const kinds: AssetNameBadgeKind[] = [
    ...(newAssetIds.has(asset.id) ? [ASSET_NAME_BADGE_KIND.NEW] : []),
    ...(asset.assetType === 'unverifiedEndpoint'
      ? [asset.unverifiedStatus]
      : isCodeDeletedLifecycle(asset.lifecycle)
        ? [ASSET_NAME_BADGE_KIND.DELETED_FROM_CODE]
        : isArchivedLifecycle(asset.lifecycle)
          ? [ASSET_NAME_BADGE_KIND.OUT_OF_SCOPE]
          : []),
  ];

  return kinds.map((kind) => <AssetNameStatusBadge key={kind} kind={kind} />);
}

const toDisplayEndpoint = (asset: EndpointAssetPreview) => ({
  ...asset,
  assetTags: asset.endpointAssetTags ?? [],
});

export function EndpointNameCell({ asset }: { asset: EndpointAssetPreview }) {
  const displayEndpoint = toDisplayEndpoint(asset);

  return (
    <AssetNameLayout
      iconType={isAiEndpoint(displayEndpoint) ? 'aiEndpoint' : 'endpoint'}
      title={getEndpointAssetTitle(displayEndpoint)}
      subtitle={getEndpointOperationName(displayEndpoint) ?? asset.description}
      badges={<AssetNameBadges asset={asset} />}
    />
  );
}

export function EndpointGraphCell({ asset }: { asset: EndpointAssetPreview }) {
  const t = useTranslations('designSystem.applicationAssets.cards');
  const { onViewGraph } = useAssetsTableContext();
  const isAi = isAiEndpoint(toDisplayEndpoint(asset));
  const hasGraph = isAi ? !!asset.aiFlowGraphId : !!asset.codeGraphId;
  const graphTooltip = isAi
    ? t('viewAiFlowGraphTitle')
    : t('viewCodeGraphTitle');
  const graphLabel = isAi ? t('viewAiFlowGraphAria') : t('viewCodeGraphAria');

  if (!hasGraph) {
    return null;
  }

  return (
    <Box display="inline-flex" {...rowClickBlockerProps}>
      <Tooltip title={graphTooltip}>
        <IconButton
          variant="ghost"
          size="xs"
          aria-label={graphLabel}
          onClick={() => onViewGraph(asset.id)}
        >
          <HierarchyIcon size={14} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

export function WebpageNameCell({ asset }: { asset: WebpageAssetPreview }) {
  return (
    <AssetNameLayout
      iconType="webPage"
      title={asset.url}
      subtitle={asset.description}
      badges={<AssetNameBadges asset={asset} />}
    />
  );
}

export function UnverifiedEndpointNameCell({
  asset,
}: {
  asset: UnverifiedEndpointAssetPreview;
}) {
  return (
    <AssetNameLayout
      iconType="endpoint"
      title={asset.path || asset.url}
      subtitle={null}
      badges={<AssetNameBadges asset={asset} />}
    />
  );
}
